import type {RemoteConfig, RemoteMount} from '@wandb/forge-shell-contract';

type RemoteContainer = {
  get(module: string): Promise<() => Promise<unknown>>;
  init?(shareScope: unknown): Promise<void>;
};

type ImportRemoteEntry = (entry: string) => Promise<RemoteContainer>;

export class RemoteConfigLoadError extends Error {
  constructor(public readonly url: string) {
    super(`Remote config script failed to load: ${url}`);
    this.name = 'RemoteConfigLoadError';
  }
}

const configLoads = new Map<string, Promise<void>>();

export function loadRemoteConfig(url: string): Promise<void> {
  const existingLoad = configLoads.get(url);
  if (existingLoad) {
    return existingLoad;
  }

  const load = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => {
      configLoads.delete(url);
      reject(new RemoteConfigLoadError(url));
    };
    document.head.appendChild(script);
  });
  configLoads.set(url, load);
  return load;
}

const importRemoteEntry: ImportRemoteEntry = async entry =>
  (await import(/* @vite-ignore */ entry)) as RemoteContainer;

const remoteMountLoads = new Map<string, Promise<RemoteMount>>();

export async function loadRemoteModule<T>(
  remote: RemoteConfig,
  moduleId: string,
  importEntry: ImportRemoteEntry = importRemoteEntry
): Promise<T> {
  switch (remote.version) {
    case 'v1':
      return loadRemoteModuleV1<T>(remote, moduleId, importEntry);
    default:
      throw new Error(
        `Unsupported remote manifest version: ${String(remote.version)}`
      );
  }
}

async function loadRemoteModuleV1<T>(
  remote: RemoteConfig,
  moduleId: string,
  importEntry: ImportRemoteEntry
): Promise<T> {
  if (remote.configScriptUrl) {
    await loadRemoteConfig(remote.configScriptUrl);
  }

  const remoteEntry = await importEntry(remote.entry);

  await remoteEntry.init?.({});
  const factory = await remoteEntry.get(moduleId);
  const module = (await factory()) as {default?: T} | T;

  if (typeof module === 'function') {
    return module as T;
  }
  if (
    typeof module === 'object' &&
    module !== null &&
    'default' in module &&
    typeof module.default === 'function'
  ) {
    return module.default as T;
  }

  throw new Error(`Remote ${remote.entry} did not expose a mount function`);
}

export function loadRemoteMount(
  remote: RemoteConfig,
  importEntry: ImportRemoteEntry = importRemoteEntry
): Promise<RemoteMount> {
  const existingLoad = remoteMountLoads.get(remote.entry);
  if (existingLoad) {
    return existingLoad;
  }

  const load = loadRemoteModule<RemoteMount>(
    remote,
    './mount-app',
    importEntry
  );
  remoteMountLoads.set(remote.entry, load);
  void load.catch(() => {
    if (remoteMountLoads.get(remote.entry) === load) {
      remoteMountLoads.delete(remote.entry);
    }
  });
  return load;
}
